import { ReactElement, useState } from 'react';
import { Component as PurpleCarrotLogoLarge } from 'icons/icon-ui-pc-logo.svg';
import { Component as PurpleCarrotLogoSmall } from 'icons/icon-ui-pc-logo-small.svg';
import { Component as AppleIcon } from 'icons/icon-social-outline--iOS.svg';
import { Component as AndroidIcon } from 'icons/icon-social-outline--Android.svg';
import { Component as FacebookIcon } from 'icons/icon-social-outline--Facebook.svg';
import { Component as PinterestIcon } from 'icons/icon-social-outline--Pintrest.svg';
import { Component as XIcon } from 'icons/icon-social-outline--X.svg';
import { Component as InstagramIcon } from 'icons/icon-social-outline--Instagram.svg';
import { Component as CheckIcon } from 'icons/icon-circle-lime--check.svg';
import { Component as AlreadySignedUpErrorIcon } from 'icons/icon-circle-lime--error.svg';
import { Component as ErrorIcon } from 'icons/icon-color--warning.svg';
import { post } from 'utils/network';
import { ApiRoute, ErrorMessage } from 'config';
import { Button } from '../shared/button/Button';
import Link from '../shared/link/Link';
import { useNotification } from '../../contexts/NotificationContext';
import { Notification } from '../shared/notification/Notification';
import { footerLinkData, footerSubSectionLinkData } from '../../constants/footer';
import { emailRegexp, Route } from '../../utils/config';
import styles from './Footer.module.scss';

function Footer(): ReactElement {
  const [email, setEmail] = useState('');
  const [isEmailInvalid, setIsEmailInvalid] = useState(false);
  const { showNotification } = useNotification();

  // ErrorIcon and AlreadySignedUpErrorIcon need div wrapper for formatting inside <Notification />
  const errorIcon = () => (
    <div>
      <ErrorIcon />
    </div>
  );
  const alreadySignedUpErrorIcon = () => (
    <div>
      <AlreadySignedUpErrorIcon />
    </div>
  );

  const handleWeeklyMenuSignUp = async (e) => {
    e.preventDefault();
    if (!emailRegexp.test(email)) {
      setIsEmailInvalid(true);
      showNotification({
        content: <Notification
          icon={errorIcon}
          text={ErrorMessage.INVALID_EMAIL}
          hasCloseButton={false}
        />,
        duration: 5000,
      });
      return;
    }

    const cookieValue = document.cookie
      .split('; ')
      .find((row) => row.startsWith('do_not_sell='))
      ?.split('=')[1];

    const response = await post(ApiRoute.WEEKLY_NEWS, {
      email,
      do_not_sell: cookieValue || false,
    });

    if (response.status === 201) {
      setIsEmailInvalid(false);

      showNotification({
        content: <Notification
          icon={CheckIcon}
          text="Success!"
          hasCloseButton={false}
        />,
        duration: 5000,
      });
      return;
    }

    if (response.status === 422) {
      setIsEmailInvalid(false);
      showNotification({
        content: <Notification
          icon={alreadySignedUpErrorIcon}
          text={ErrorMessage.ALREADY_SUBSCRIBED}
          hasCloseButton={false}
        />,
        duration: 5000,
      });
    } else if (!response.ok) {
      showNotification({
        content: <Notification
          icon={errorIcon}
          text="Error during email signup."
          hasCloseButton={false}
        />,
        duration: 5000,
      });
    }
  };

  const finalLinkColumn = (
    <div className={`${styles.link_column} ${styles.final_link_column}`} key="final-column">
      <div>
        <div className={styles.link_title}>Download the App</div>
        <Link
          href={Route.APPLE_DOWNLOAD}
          reload
          className={styles.link}
        >
          <AppleIcon />
          iOS
        </Link>
        <Link
          href={Route.ANDROID_DOWNLOAD}
          reload
          className={styles.link}
        >
          <AndroidIcon />
          Android
        </Link>
      </div>
      <div className={styles.weeklyMenuSignUpContainer}>
        <div className={styles.link_title}>Weekly Menu Sign Up</div>
        <form
          className="simple_form new_weekly_news_form"
          onSubmit={handleWeeklyMenuSignUp}
        >
          <div
            className={[
              'c-form-group',
              'email',
              'required',
              'weekly_news_form_email',
              isEmailInvalid ? 'is-invalid' : '',
              styles.weeklyMenuSignUp,
            ].join(' ')}
          >
            <input
              id="email_for_weekly_menu"
              className="string email required"
              placeholder="@email"
              type="email"
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button text="→" type="submit" />
          </div>
        </form>
      </div>
    </div>
  );

  const linkColumns = footerLinkData.map((column, index) => {
    const links = column.links.map((link) => (
      <Link
        key={link.label}
        href={link.link}
        reload={link.reload || false}
        className={styles.link}
      >
        {link.label}
      </Link>
    ));

    return (
      <div className={styles.link_column} key={column.title}>
        {index === 0 && (
          <div className={styles.logo_small}>
            <PurpleCarrotLogoSmall />
          </div>
        )}
        <div className={styles.link_title}>{column.title}</div>
        {links}
      </div>
    );
  })
    .concat(finalLinkColumn);

  return (
    <footer className={styles.footer}>
      <div className={styles.footer_main}>
        <div className={styles.logo}>
          <PurpleCarrotLogoLarge />
        </div>

        <div className={styles.linkColumns}>
          {linkColumns}
        </div>
      </div>

      <div className={styles.footer_subsection}>
        <div className={styles.footer_subsection__text}>
          <div className={styles.toc_text}>
            {footerSubSectionLinkData?.map(([label, href], i) => (
              <span key={label} className={styles.toc_item}>
                <Link href={href} reload>{label}</Link>
                {i < footerSubSectionLinkData.length - 1
                  && <span className={styles.separator}> | </span>}
              </span>
            ))}
          </div>
          <div className={styles.copyright_text}>
            © 2026 Purple Carrot. All rights reserved.
          </div>
        </div>

        <div className={styles.social_icons}>
          <a href={Route.FACEBOOK}>
            <FacebookIcon />
          </a>
          <a href={Route.PINTEREST}>
            <PinterestIcon />
          </a>
          <a href={Route.TWITTER}>
            <XIcon />
          </a>
          <a href={Route.INSTAGRAM}>
            <InstagramIcon />
          </a>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
